import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AudioAnalysisPanel } from '../components/AudioAnalysisPanel';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Upload, 
  Loader2, 
  Sparkles,
  X,
  CheckCircle2,
  ArrowRight,
  Cpu,
  Brain,
  Tag,
  Trash2,
  Check,
  Shield,
  AlertTriangle,
  TrendingUp,
  Target,
  Video,
  Image as ImageIcon,
  Film,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Competitor brands for competitive analysis
const COMPETITOR_BRANDS = ['MuscleBlaze', 'Superyou', 'ON', 'GNC'];

const ANALYSIS_STEPS = [
  { text: "🚀 Waking up the AI brain...", subtext: "Neural networks initializing" },
  { text: "👁️ AI is looking at your creative...", subtext: "Computer vision activated" },
  { text: "🔍 Scanning every pixel...", subtext: "Deep image analysis in progress" },
  { text: "📝 Reading text & headlines...", subtext: "OCR engine processing" },
  { text: "🎨 Detecting colors & vibes...", subtext: "Color palette extraction" },
  { text: "🧠 Understanding the message...", subtext: "Semantic analysis running" },
  { text: "✨ Extracting creative DNA...", subtext: "Pattern recognition complete" },
  { text: "📊 Generating insights...", subtext: "Almost there!" },
];

const VIDEO_ANALYSIS_STEPS = [
  { text: "🎬 Processing your video...", subtext: "Preparing frames for analysis" },
  { text: "🖼️ Extracting key frames...", subtext: "Capturing important moments" },
  { text: "👁️ AI analyzing scenes...", subtext: "Computer vision activated" },
  { text: "🔍 Understanding the story...", subtext: "Narrative analysis in progress" },
  { text: "📝 Reading text & CTAs...", subtext: "OCR engine processing" },
  { text: "🎨 Detecting colors & mood...", subtext: "Visual style extraction" },
  { text: "✨ Extracting video DNA...", subtext: "Pattern recognition complete" },
  { text: "📊 Generating insights...", subtext: "Almost there!" },
];

const CreativeAnalyzerPage = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]); // [{file, preview, brand, competitor, selected, isVideo}]
  const [analyzing, setAnalyzing] = useState(false);
  // Per-file transient status shown to the user during bulk uploads:
  // 'uploading' | 'queued' (waiting for the backend serial queue) | 'processing' | 'done' | 'failed'
  // The map is keyed by file index. Kept alongside `bulkProgress` so the
  // existing progress bar keeps working — this just adds richer per-item state.
  const [fileStatuses, setFileStatuses] = useState({});
  // If the backend tells us "N videos ahead of you", surface it verbatim so
  // multi-user scenarios feel understandable (see /api/analyze-video/status).
  const [queueAhead, setQueueAhead] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const frames = analysisResult?.analyzed_frames || [];
  const lightboxFrame = lightboxIdx !== null ? frames[lightboxIdx] : null;
  const closeLightbox = () => setLightboxIdx(null);
  const prevFrame = useCallback(() => {
    setLightboxIdx((i) => (i === null || frames.length === 0 ? i : (i - 1 + frames.length) % frames.length));
  }, [frames.length]);
  const nextFrame = useCallback(() => {
    setLightboxIdx((i) => (i === null || frames.length === 0 ? i : (i + 1) % frames.length));
  }, [frames.length]);
  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prevFrame();
      else if (e.key === 'ArrowRight') nextFrame();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIdx, prevFrame, nextFrame]);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [brands, setBrands] = useState([]);
  const [bulkBrand, setBulkBrand] = useState('');
  const [bulkCompetitor, setBulkCompetitor] = useState('');
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  
  // Media type toggle (image or video)
  const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
  
  // Validation tab state
  const [activeTab, setActiveTab] = useState('analyze');
  const [validateFile, setValidateFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationHistory, setValidationHistory] = useState([]);

  useEffect(() => {
    fetchBrands();
    fetchValidationHistory();
  }, []);

  const fetchBrands = async () => {
    try {
      // Use global master data brands
      const response = await fetch(`${API_URL}/api/masters/brands`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  const fetchValidationHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/creative-validations`, {
        credentials: 'include'
      });
      if (response.ok) {
        setValidationHistory(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch validation history:', error);
    }
  };

  const handleValidateCreative = async () => {
    if (!validateFile) return;
    
    setValidating(true);
    setValidationResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', validateFile);
      
      const response = await fetch(`${API_URL}/api/creative-analyses/validate`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Validation failed');
      }
      
      const result = await response.json();
      setValidationResult(result.validation);
      fetchValidationHistory();
      toast.success('Creative validated successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setValidating(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const acceptedTypes = mediaType === 'video' 
      ? ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mpeg']
      : ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    
    const newFiles = Array.from(e.dataTransfer.files)
      .filter(f => acceptedTypes.includes(f.type))
      .map(f => ({ 
        file: f, 
        preview: URL.createObjectURL(f), 
        brand: '', 
        competitor: '', 
        selected: false,
        // Trust the file's real MIME type, not just the mode toggle. Prevents
        // a video from being sent to the image endpoint if the user forgot to
        // flip the toggle.
        isVideo: mediaType === 'video' || (f.type || '').startsWith('video/')
      }));
    
    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    } else {
      toast.error(`Please upload ${mediaType === 'video' ? 'video' : 'image'} files only`);
    }
  }, [mediaType]);

  const handleFileSelect = (e) => {
    const acceptedTypes = mediaType === 'video' 
      ? ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mpeg']
      : ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    
    const newFiles = Array.from(e.target.files)
      .filter(f => acceptedTypes.includes(f.type))
      .map(f => ({ 
        file: f, 
        preview: URL.createObjectURL(f), 
        brand: '', 
        competitor: '', 
        selected: false,
        isVideo: mediaType === 'video' || (f.type || '').startsWith('video/')
      }));
    
    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const toggleSelect = (index) => {
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, selected: !f.selected } : f));
  };

  const selectAll = () => {
    const allSelected = files.every(f => f.selected);
    setFiles(prev => prev.map(f => ({ ...f, selected: !allSelected })));
  };

  const applyBrandToSelected = () => {
    if (!bulkBrand) return;
    const selectedCount = files.filter(f => f.selected).length;
    if (selectedCount === 0) {
      toast.error('Please select images first');
      return;
    }
    setFiles(prev => prev.map(f => f.selected ? { ...f, brand: bulkBrand } : f));
    toast.success(`Applied "${bulkBrand}" to ${selectedCount} images`);
  };

  const applyCompetitorToSelected = () => {
    if (!bulkCompetitor) return;
    const selectedCount = files.filter(f => f.selected).length;
    if (selectedCount === 0) {
      toast.error('Please select images first');
      return;
    }
    setFiles(prev => prev.map(f => f.selected ? { ...f, competitor: bulkCompetitor } : f));
    toast.success(`Applied "${bulkCompetitor}" to ${selectedCount} images`);
  };

  const clearAll = () => {
    setFiles([]);
    setAnalysisResult(null);
  };

  const selectedCount = files.filter(f => f.selected).length;

  const analyzeCreatives = async () => {
    if (files.length === 0) return;

    setAnalyzing(true);
    setBulkProgress({ current: 0, total: files.length });
    setProgress(0);
    setCurrentStep(0);
    setFileStatuses({});
    setQueueAhead({});

    let successCount = 0;
    let failCount = 0;
    let lastResult = null;
    let lastError = null;
    
    // Determine which steps to use based on file types
    const hasVideos = files.some(f => f.isVideo);
    const steps = hasVideos ? VIDEO_ANALYSIS_STEPS : ANALYSIS_STEPS;

    // Start progress animation
    const progressInterval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length);
    }, 800);

    for (let i = 0; i < files.length; i++) {
      const { file, brand, competitor, isVideo } = files[i];
      setBulkProgress({ current: i + 1, total: files.length });
      setProgress(((i + 1) / files.length) * 100);
      setFileStatuses(s => ({ ...s, [i]: 'uploading' }));

      try {
        let response;
        // For videos larger than ~30MB, use chunked upload to bypass browser
        // ServiceWorker postMessage clone limits (browsers throw "Request could
        // not be cloned" for big multipart bodies). For everything else, use
        // the regular single-POST path.
        const CHUNK_THRESHOLD = 30 * 1024 * 1024; // 30 MB
        const useChunked = isVideo && file.size > CHUNK_THRESHOLD;

        // Polls an async analysis job until it finishes. Returns a Response-like
        // Server-side jobs run through a single-slot semaphore. We track
        // queued vs. running here so the UI can say "Queued — 2 videos ahead"
        // before flipping to "Analyzing…" once the backend actually picks it up.
        const pollVideoJob = async (jobId) => {
          // Poll every 2.5s, up to ~10 minutes.
          const MAX_ATTEMPTS = 240;
          for (let a = 0; a < MAX_ATTEMPTS; a++) {
            await new Promise(res => setTimeout(res, 2500));
            const sr = await fetch(`${API_URL}/api/analyze-video/status/${jobId}`, { credentials: 'include' });
            if (!sr.ok) {
              return { ok: false, status: sr.status, json: async () => ({ detail: `Status poll failed (HTTP ${sr.status})` }) };
            }
            const sj = await sr.json();
            if (sj.status === 'success' && sj.result) {
              setFileStatuses(s => ({ ...s, [i]: 'done' }));
              setQueueAhead(q => { const n = { ...q }; delete n[i]; return n; });
              return { ok: true, status: 200, json: async () => sj.result };
            }
            if (sj.status === 'error') {
              setFileStatuses(s => ({ ...s, [i]: 'failed' }));
              setQueueAhead(q => { const n = { ...q }; delete n[i]; return n; });
              return { ok: false, status: sj.http_status || 500, json: async () => ({ detail: sj.error || 'Analysis failed' }) };
            }
            // In-flight — reflect the queued vs. running transition so the
            // user knows whether the delay is our own queue or the AI.
            if (sj.status === 'queued') {
              setFileStatuses(s => (s[i] === 'queued' ? s : { ...s, [i]: 'queued' }));
              if (typeof sj.queue_position === 'number') {
                setQueueAhead(q => ({ ...q, [i]: sj.queue_position }));
              }
            } else if (sj.status === 'running') {
              setFileStatuses(s => (s[i] === 'processing' ? s : { ...s, [i]: 'processing' }));
              setQueueAhead(q => { const n = { ...q }; delete n[i]; return n; });
            }
            // Update sub-progress to keep the UI moving during long analyses.
            const subPct = Math.min(0.98, 0.15 + (a / MAX_ATTEMPTS) * 0.83);
            setProgress(((i + subPct) / files.length) * 100);
          }
          setFileStatuses(s => ({ ...s, [i]: 'failed' }));
          return { ok: false, status: 504, json: async () => ({ detail: 'Analysis timed out after 10 minutes' }) };
        };

        if (useChunked) {
          // 1. Init
          const initRes = await fetch(`${API_URL}/api/uploads/video/init`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              filename: file.name,
              content_type: file.type || 'video/mp4',
              total_chunks: Math.ceil(file.size / (5 * 1024 * 1024)),
              size: file.size,
            }),
          });
          if (!initRes.ok) {
            const ed = await initRes.json().catch(() => ({}));
            throw new Error(ed.detail || `Init failed: HTTP ${initRes.status}`);
          }
          const { upload_id } = await initRes.json();

          // 2. Upload chunks of 5MB
          const CHUNK_SIZE = 5 * 1024 * 1024;
          const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
          for (let idx = 0; idx < totalChunks; idx++) {
            const start = idx * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const blob = file.slice(start, end);
            const fd = new FormData();
            fd.append('index', String(idx));
            fd.append('chunk', blob, `part_${idx}.bin`);
            const cr = await fetch(`${API_URL}/api/uploads/video/${upload_id}/chunk`, {
              method: 'POST',
              credentials: 'include',
              body: fd,
            });
            if (!cr.ok) {
              const ed = await cr.json().catch(() => ({}));
              throw new Error(ed.detail || `Chunk ${idx} failed: HTTP ${cr.status}`);
            }
            // Update progress sub-step within this file: chunks contribute up to ~15% of this file's slice
            const subPct = ((idx + 1) / totalChunks) * 0.15;
            setProgress(((i + subPct) / files.length) * 100);
          }

          // 3. Kick off async analysis job (returns job_id instantly — avoids CF 504)
          const startRes = await fetch(`${API_URL}/api/uploads/video/${upload_id}/finalize-and-analyze-start`, {
            method: 'POST',
            credentials: 'include',
          });
          if (!startRes.ok) {
            const ed = await startRes.json().catch(() => ({}));
            throw new Error(ed.detail || `Start failed: HTTP ${startRes.status}`);
          }
          const { job_id } = await startRes.json();
          response = await pollVideoJob(job_id);
        } else if (isVideo) {
          // Small video: use async start/poll path too (frames + GPT-4o can still exceed 100s)
          const formData = new FormData();
          formData.append('file', file);
          const startRes = await fetch(`${API_URL}/api/analyze-video/start`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
          if (!startRes.ok) {
            const ed = await startRes.json().catch(() => ({}));
            throw new Error(ed.detail || `Start failed: HTTP ${startRes.status}`);
          }
          const { job_id } = await startRes.json();
          response = await pollVideoJob(job_id);
        } else {
          const formData = new FormData();
          formData.append('file', file);
          response = await fetch(`${API_URL}/api/analyze-creative`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
        }

        if (response.ok) {
          const result = await response.json();
          lastResult = result;
          
          // Update brand if set
          if (brand && result.analysis_id) {
            await fetch(`${API_URL}/api/creative-analyses/${result.analysis_id}/brand`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ brand })
            });
          }
          
          // Update competitor if set
          if (competitor && result.analysis_id) {
            await fetch(`${API_URL}/api/creative-analyses/${result.analysis_id}/competitor`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ competitor })
            });
          }
          successCount++;
          setFileStatuses(s => (s[i] === 'done' ? s : { ...s, [i]: 'done' }));
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Analysis failed:', errorData);
          lastError = errorData?.detail || `HTTP ${response.status}`;
          failCount++;
          setFileStatuses(s => (s[i] === 'failed' ? s : { ...s, [i]: 'failed' }));
        }
      } catch (error) {
        console.error('Analysis error:', error);
        lastError = error?.message || 'Network error';
        failCount++;
        setFileStatuses(s => ({ ...s, [i]: 'failed' }));
      }
    }

    clearInterval(progressInterval);
    setAnalyzing(false);
    setBulkProgress({ current: 0, total: 0 });
    setProgress(0);
    
    const mediaLabel = files.some(f => f.isVideo) ? 'creatives' : 'images';
    
    if (successCount > 0) {
      if (files.length === 1 && lastResult) {
        setAnalysisResult(lastResult);
        setShowResultModal(true);
      } else {
        toast.success(`Successfully analyzed ${successCount} ${mediaLabel}`);
        navigate('/creative-log');
      }
      setFiles([]);
    }
    if (failCount > 0) {
      toast.error(lastError
        ? `Failed to analyze ${failCount} ${mediaLabel}: ${lastError}`
        : `Failed to analyze ${failCount} ${mediaLabel}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="creative-analyzer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Creative Analyzer</h1>
            <p className="text-sm text-slate-500">AI-powered ad creative analysis & validation</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="analyze" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Analyze Creative
          </TabsTrigger>
          <TabsTrigger value="validate" className="flex items-center gap-2">
            <Shield className="h-4 w-4" /> Validate Creative
          </TabsTrigger>
        </TabsList>

        {/* ANALYZE TAB */}
        <TabsContent value="analyze">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              {analyzing ? (
                /* Futuristic AI Analysis Progress UI */
                <div className="py-12 px-4">
                  <div className="max-w-md mx-auto text-center">
                    {/* Animated AI visualization */}
                    <div className="relative mb-8">
                      {/* Outer rotating ring */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-36 h-36 rounded-full border-4 border-dashed border-violet-300 animate-[spin_8s_linear_infinite]" />
                      </div>
                      {/* Middle pulsing ring */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-28 h-28 rounded-full bg-gradient-to-r from-violet-500/20 to-purple-500/20 animate-pulse" />
                      </div>
                      {/* Center brain */}
                      <div className="relative flex items-center justify-center h-36">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-violet-500/50">
                          <Brain className="h-10 w-10 text-white animate-pulse" />
                        </div>
                        {/* Floating particles */}
                        <div className="absolute top-2 right-8 w-3 h-3 rounded-full bg-amber-400 animate-bounce" style={{animationDelay: '0s'}} />
                        <div className="absolute bottom-4 left-6 w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{animationDelay: '0.5s'}} />
                        <div className="absolute top-8 left-4 w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay: '1s'}} />
                        <div className="absolute bottom-2 right-6 w-3 h-3 rounded-full bg-rose-400 animate-bounce" style={{animationDelay: '0.3s'}} />
                      </div>
                    </div>

                    {/* Model badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-gradient-to-r from-slate-900 to-slate-800 text-white text-sm font-medium">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      GPT-4o Vision Active
                    </div>

                    {/* Progress info */}
                    {files.length > 1 && (
                      <p className="text-lg font-bold text-slate-800 mb-2">
                        Analyzing {bulkProgress.current} of {bulkProgress.total}
                      </p>
                    )}

                    {/* Current step - catchy text */}
                    <div className="mb-4">
                      <p className="text-xl font-bold text-slate-800 mb-1">
                    {(files.some(f => f.isVideo) ? VIDEO_ANALYSIS_STEPS : ANALYSIS_STEPS)[currentStep]?.text}
                  </p>
                  <p className="text-sm text-slate-500">
                    {(files.some(f => f.isVideo) ? VIDEO_ANALYSIS_STEPS : ANALYSIS_STEPS)[currentStep]?.subtext}
                  </p>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <Progress value={progress} className="h-3 bg-slate-100" />
                </div>
                <p className="text-sm font-semibold text-violet-600 mb-6">
                  {Math.round(progress)}% complete
                </p>

                {/* Terminal style feed */}
                <div className="bg-slate-900 rounded-xl p-4 text-left font-mono text-xs">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-slate-500 ml-2">AI Terminal</span>
                  </div>
                  <div className="space-y-1 text-green-400">
                    <p><span className="text-slate-500">→</span> model.load(<span className="text-amber-400">"gpt-4o-vision"</span>)</p>
                    <p><span className="text-slate-500">→</span> image.analyze(<span className="text-amber-400">creative_{bulkProgress.current}.jpg</span>)</p>
                    <p className="flex items-center gap-2">
                      <span className="text-slate-500">→</span> 
                      extracting_elements
                      <span className="inline-block w-2 h-4 bg-green-400 animate-pulse" />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Media Type Toggle - Elegant Pills */}
              <div className="flex items-center justify-center">
                <div className="inline-flex items-center p-1 bg-slate-100 rounded-full">
                  <button
                    onClick={() => { setMediaType('image'); setFiles([]); }}
                    className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      mediaType === 'image' 
                        ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <ImageIcon className="h-4 w-4" />
                    Image
                  </button>
                  <button
                    onClick={() => { setMediaType('video'); setFiles([]); }}
                    className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      mediaType === 'video' 
                        ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Film className="h-4 w-4" />
                    Video
                  </button>
                </div>
              </div>

              {/* Actions Bar - show when files exist */}
              {files.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="flex items-center gap-1">
                      {mediaType === 'video' ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                      {files.length} {mediaType}{files.length > 1 ? 's' : ''}
                    </Badge>
                    
                    <Button size="sm" variant="outline" onClick={selectAll} className="h-8">
                      <Check className="h-3 w-3 mr-1" />
                      {files.every(f => f.selected) ? 'Deselect All' : 'Select All'}
                    </Button>
                    
                    {selectedCount > 0 && (
                      <>
                        <span className="text-xs text-slate-500">|</span>
                        <span className="text-xs text-violet-600 font-medium">{selectedCount} selected</span>
                        <Select value={bulkBrand} onValueChange={setBulkBrand}>
                          <SelectTrigger className="w-[140px] h-8">
                            <SelectValue placeholder="Choose brand" />
                          </SelectTrigger>
                          <SelectContent>
                            {brands.map(b => (
                              <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={applyBrandToSelected} disabled={!bulkBrand} className="h-8">
                          <Tag className="h-3 w-3 mr-1" /> Apply Brand
                        </Button>
                        <span className="text-xs text-slate-500">|</span>
                        <Select value={bulkCompetitor} onValueChange={setBulkCompetitor}>
                          <SelectTrigger className="w-[150px] h-8">
                            <SelectValue placeholder="Tag competitor" />
                          </SelectTrigger>
                          <SelectContent>
                            {COMPETITOR_BRANDS.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={applyCompetitorToSelected} disabled={!bulkCompetitor} className="h-8">
                          <Tag className="h-3 w-3 mr-1" /> Apply Competitor
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={clearAll}>
                      <Trash2 className="h-4 w-4 mr-1" /> Clear
                    </Button>
                    <Button size="sm" onClick={analyzeCreatives} className="bg-violet-600 hover:bg-violet-700">
                      <Sparkles className="h-4 w-4 mr-1" /> Analyze {files.length > 1 ? 'All' : ''}
                    </Button>
                  </div>
                </div>
              )}

              {/* Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-xl transition-all ${
                  dragActive ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-violet-300'
                } ${files.length > 0 ? 'p-4' : 'p-0'}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {files.length === 0 ? (
                  <label className="flex flex-col items-center justify-center py-16 cursor-pointer">
                    {mediaType === 'video' ? (
                      /* Elegant Video Frames Visual */
                      <div className="relative mb-6">
                        <div className="flex items-center gap-1">
                          {/* Film strip frames */}
                          <div className="w-12 h-16 rounded bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-slate-300 flex items-center justify-center transform -rotate-6 shadow-sm">
                            <div className="w-8 h-10 rounded-sm bg-gradient-to-br from-violet-100 to-purple-100" />
                          </div>
                          <div className="w-14 h-20 rounded bg-gradient-to-br from-violet-400 to-purple-500 border-2 border-violet-300 flex items-center justify-center shadow-lg z-10">
                            <Film className="h-7 w-7 text-white" />
                          </div>
                          <div className="w-12 h-16 rounded bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-slate-300 flex items-center justify-center transform rotate-6 shadow-sm">
                            <div className="w-8 h-10 rounded-sm bg-gradient-to-br from-violet-100 to-purple-100" />
                          </div>
                        </div>
                        {/* Animated sparkles */}
                        <div className="absolute -top-2 -right-2 w-4 h-4 text-amber-400 animate-pulse">
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"/></svg>
                        </div>
                        <div className="absolute -bottom-1 -left-3 w-3 h-3 text-violet-400 animate-pulse delay-300">
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"/></svg>
                        </div>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mb-4">
                        <Upload className="h-10 w-10 text-violet-500" />
                      </div>
                    )}
                    <p className="text-lg font-medium text-slate-700 mb-1">
                      Drop your {mediaType === 'video' ? 'videos' : 'creatives'} here or click to upload
                    </p>
                    <p className="text-sm text-slate-500">
                      {mediaType === 'video' 
                        ? 'Supports MP4, MOV, WebM, AVI (max 100MB) • AI extracts key frames for analysis'
                        : 'Supports JPEG, PNG, WebP (max 10MB) • Single or multiple files'
                      }
                    </p>
                    {mediaType === 'video' && (
                      <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-400"></span> Frame 1</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-300"></span> Frame 2</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-200"></span> Frame 3</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-300"></span> Frame 4</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400"></span> Frame 5</span>
                      </div>
                    )}
                    <input 
                      type="file" 
                      accept={mediaType === 'video' ? 'video/mp4,video/quicktime,video/webm,video/x-msvideo,video/mpeg' : 'image/*'} 
                      multiple 
                      onChange={handleFileSelect} 
                      className="hidden" 
                    />
                  </label>
                ) : (
                  <div>
                    {/* Media Grid with checkboxes */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      {files.map((item, index) => (
                        <div 
                          key={index} 
                          className={`relative group bg-slate-100 rounded-lg overflow-hidden border-2 transition-all ${
                            item.selected ? 'border-violet-500 ring-2 ring-violet-200' : 'border-transparent'
                          }`}
                        >
                          {/* Checkbox */}
                          <div 
                            className="absolute top-2 left-2 z-10 cursor-pointer"
                            onClick={() => toggleSelect(index)}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              item.selected 
                                ? 'bg-violet-500 border-violet-500' 
                                : 'bg-white/90 border-slate-300 hover:border-violet-400'
                            }`}>
                              {item.selected && <Check className="h-3 w-3 text-white" />}
                            </div>
                          </div>

                          {/* Video indicator */}
                          {item.isVideo && (
                            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10">
                              <div className="px-2 py-0.5 bg-violet-600 text-white text-[10px] font-medium rounded flex items-center gap-1">
                                <Film className="h-3 w-3" /> Video
                              </div>
                            </div>
                          )}

                          {/* Per-file analysis status — mirrors the backend
                              serial queue so users can see queued/processing
                              /done/failed for each individual upload. */}
                          {analyzing && fileStatuses[index] && (
                            <div
                              className="absolute inset-x-2 top-8 z-10 flex justify-center pointer-events-none"
                              data-testid={`file-status-${index}`}
                            >
                              <div className={`px-2 py-0.5 text-[10px] font-medium rounded-full shadow flex items-center gap-1 ${
                                fileStatuses[index] === 'done'
                                  ? 'bg-emerald-500 text-white'
                                  : fileStatuses[index] === 'failed'
                                  ? 'bg-red-500 text-white'
                                  : fileStatuses[index] === 'processing'
                                  ? 'bg-sky-500 text-white'
                                  : fileStatuses[index] === 'queued'
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-slate-500 text-white'
                              }`}>
                                {fileStatuses[index] === 'queued'
                                  ? (queueAhead[index] > 0
                                      ? `Queued · ${queueAhead[index]} ahead`
                                      : 'Queued')
                                  : fileStatuses[index] === 'processing'
                                  ? 'Analyzing…'
                                  : fileStatuses[index] === 'uploading'
                                  ? 'Uploading…'
                                  : fileStatuses[index] === 'done'
                                  ? 'Done'
                                  : 'Failed'}
                              </div>
                            </div>
                          )}
                          {/* Remove button */}
                          <button
                            onClick={() => removeFile(index)}
                            className="absolute top-2 right-2 z-10 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          
                          {item.isVideo ? (
                            <video src={item.preview} className="w-full aspect-square object-cover" />
                          ) : (
                            <img src={item.preview} alt="" className="w-full aspect-square object-cover" />
                          )}
                          
                          {/* Brand & Competitor indicators */}
                          <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent">
                            <div className="flex flex-wrap gap-1">
                              {item.brand ? (
                                <span className="px-2 py-0.5 rounded bg-emerald-500 text-white text-[10px] font-medium">
                                  {item.brand}
                                </span>
                              ) : null}
                              {item.competitor ? (
                                <span className="px-2 py-0.5 rounded bg-orange-500 text-white text-[10px] font-medium">
                                  {item.competitor}
                                </span>
                              ) : null}
                              {!item.brand && !item.competitor && (
                                <span className="text-[10px] text-white/70">No tags</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add more button */}
                      <label className="aspect-square border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-violet-300 hover:bg-violet-50 transition-all">
                        <Upload className="h-6 w-6 text-slate-400 mb-1" />
                        <span className="text-xs text-slate-500">Add more</span>
                        <input type="file" accept={mediaType === 'video' ? 'video/*' : 'image/*'} multiple onChange={handleFileSelect} className="hidden" />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
          </Card>
        </TabsContent>

        {/* VALIDATE TAB */}
        <TabsContent value="validate">
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="max-w-2xl mx-auto">
                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Validate Against Winning Formula</p>
                      <p className="text-xs text-blue-700 mt-1">
                        Upload a new creative you made based on performance insights. AI will check if it follows the winning patterns and predict expected ROAS.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Upload Area */}
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center mb-6 hover:border-violet-300 transition-colors">
                  {validateFile ? (
                    <div className="space-y-4">
                      <img 
                        src={URL.createObjectURL(validateFile)} 
                        alt="Preview" 
                        className="max-h-64 mx-auto rounded-lg shadow-md"
                      />
                      <p className="text-sm text-slate-600">{validateFile.name}</p>
                      <Button variant="outline" size="sm" onClick={() => { setValidateFile(null); setValidationResult(null); }}>
                        <X className="h-4 w-4 mr-1" /> Remove
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">Drop your creative here</p>
                      <p className="text-xs text-slate-400 mt-1">or click to browse</p>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => { 
                          if (e.target.files?.[0]) {
                            setValidateFile(e.target.files[0]);
                            setValidationResult(null);
                          }
                        }} 
                        className="hidden" 
                      />
                    </label>
                  )}
                </div>

                {/* Validate Button */}
                <Button 
                  onClick={handleValidateCreative}
                  disabled={!validateFile || validating}
                  className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-base"
                >
                  {validating ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Validating against winning formula...
                    </>
                  ) : (
                    <>
                      <Shield className="h-5 w-5 mr-2" />
                      Validate Creative
                    </>
                  )}
                </Button>

                {/* Validation Result */}
                {validationResult && (
                  <div className="mt-6 space-y-4">
                    {/* Score Card */}
                    <div className={`rounded-xl p-6 ${
                      validationResult.alignment_score >= 70 ? 'bg-emerald-50 border border-emerald-200' :
                      validationResult.alignment_score >= 40 ? 'bg-amber-50 border border-amber-200' :
                      'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase">Alignment Score</p>
                          <p className={`text-4xl font-bold ${
                            validationResult.alignment_score >= 70 ? 'text-emerald-600' :
                            validationResult.alignment_score >= 40 ? 'text-amber-600' :
                            'text-red-600'
                          }`}>{validationResult.alignment_score}/100</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-medium text-slate-500 uppercase">Predicted ROAS</p>
                          <p className="text-2xl font-bold text-slate-800">{validationResult.predicted_roas}</p>
                        </div>
                      </div>
                      <Badge className={`${
                        validationResult.verdict === 'Strong Match' ? 'bg-emerald-500' :
                        validationResult.verdict === 'Good' ? 'bg-blue-500' :
                        'bg-amber-500'
                      } text-white`}>
                        {validationResult.verdict}
                      </Badge>
                    </div>

                    {/* Summary */}
                    {validationResult.summary && (
                      <div className="bg-slate-50 rounded-xl p-4 border">
                        <p className="text-sm text-slate-700">{validationResult.summary}</p>
                      </div>
                    )}

                    {/* Present vs Missing */}
                    <div className="grid grid-cols-2 gap-4">
                      {validationResult.present_elements?.length > 0 && (
                        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                          <h4 className="text-xs font-bold text-emerald-700 uppercase mb-2 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Present Elements
                          </h4>
                          <ul className="space-y-1">
                            {validationResult.present_elements.map((el, i) => (
                              <li key={i} className="text-sm text-slate-700">• {el}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {validationResult.missing_elements?.length > 0 && (
                        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                          <h4 className="text-xs font-bold text-amber-700 uppercase mb-2 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> Missing Elements
                          </h4>
                          <ul className="space-y-1">
                            {validationResult.missing_elements.map((el, i) => (
                              <li key={i} className="text-sm text-slate-700">• {el}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Problematic Elements */}
                    {validationResult.problematic_elements?.length > 0 && (
                      <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                        <h4 className="text-xs font-bold text-red-700 uppercase mb-2 flex items-center gap-1">
                          <X className="h-3 w-3" /> Should Be Avoided
                        </h4>
                        <ul className="space-y-1">
                          {validationResult.problematic_elements.map((el, i) => (
                            <li key={i} className="text-sm text-slate-700">• {el}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Recommendations */}
                    {validationResult.recommendations?.length > 0 && (
                      <div className="bg-violet-50 rounded-xl p-4 border border-violet-200">
                        <h4 className="text-xs font-bold text-violet-700 uppercase mb-2 flex items-center gap-1">
                          <Target className="h-3 w-3" /> Recommendations
                        </h4>
                        <ul className="space-y-1">
                          {validationResult.recommendations.map((rec, i) => (
                            <li key={i} className="text-sm text-slate-700">{i + 1}. {rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Powered by */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
        <Cpu className="h-3 w-3" />
        <span>Powered by <span className="font-medium text-slate-600">GPT-4o Vision</span></span>
      </div>

      {/* Result Modal - for single image */}
      <Dialog open={showResultModal} onOpenChange={setShowResultModal}>
        <DialogContent className={`${analysisResult?.is_video && analysisResult?.audio_info ? 'w-[96vw] max-w-[1500px]' : 'max-w-2xl'} max-h-[90vh] flex flex-col p-0 gap-0`}>
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Analysis Complete
            </DialogTitle>
          </DialogHeader>
          {analysisResult && (
            <div className={`flex-1 min-h-0 overflow-hidden grid ${analysisResult.is_video && analysisResult.audio_info ? 'grid-cols-1 md:grid-cols-[minmax(0,1fr)_420px]' : 'grid-cols-1'}`}>
              {/* LEFT — frames + main analysis (scrollable) */}
              <div className="space-y-4 px-6 py-4 overflow-y-auto min-h-0">
              {/* Analyzed frames carousel (videos only) */}
              {analysisResult.is_video && Array.isArray(analysisResult.analyzed_frames) && analysisResult.analyzed_frames.length > 0 && (
                <div data-testid="analyzed-frames-carousel">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Frames analyzed by AI · {analysisResult.analyzed_frames.length}
                    </p>
                    <span className="text-[10px] text-slate-400">click to expand</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
                    {analysisResult.analyzed_frames.map((f, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setLightboxIdx(idx)}
                        className="relative flex-shrink-0 w-40 snap-start rounded-lg overflow-hidden border border-slate-200 bg-slate-100 group hover:border-violet-400 hover:shadow-md transition-all cursor-zoom-in"
                        data-testid={`analyzed-frame-${idx}`}
                      >
                        <img
                          src={`${API_URL}${f.url}`}
                          alt={`Frame at ${f.label}`}
                          className="w-full h-24 object-cover group-hover:opacity-90 transition-opacity"
                          loading="lazy"
                        />
                        <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-semibold">
                          {f.label}
                        </div>
                        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-white/90 text-slate-700 text-[10px] font-mono">
                          {f.timestamp_sec}s
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {analysisResult.analysis?.theme && (
                  <Badge className="bg-violet-100 text-violet-700">{analysisResult.analysis.theme}</Badge>
                )}
                {analysisResult.analysis?.style && (
                  <Badge className="bg-blue-100 text-blue-700">{analysisResult.analysis.style}</Badge>
                )}
                {analysisResult.analysis?.emotion && (
                  <Badge className="bg-rose-100 text-rose-700">{analysisResult.analysis.emotion}</Badge>
                )}
              </div>
              {analysisResult.analysis?.headline && analysisResult.analysis.headline !== 'Not visible' && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Headline</p>
                  <p className="font-medium text-slate-900">{analysisResult.analysis.headline}</p>
                </div>
              )}
              {analysisResult.cost && (
                <div className="flex items-center gap-2 pt-2 border-t text-xs text-slate-500">
                  <span>Cost: ${analysisResult.cost.total_cost_usd?.toFixed(4)}</span>
                  <span>•</span>
                  <span>{analysisResult.cost.total_tokens?.toLocaleString()} tokens</span>
                </div>
              )}
              </div>
              {/* RIGHT — Audio Intelligence panel (videos with audio only) */}
              {analysisResult.is_video && analysisResult.audio_info && (
                <div className="hidden md:block border-l border-slate-200/70 bg-slate-50/40 overflow-y-auto min-h-0 p-4">
                  <AudioAnalysisPanel audioInfo={analysisResult.audio_info} />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0">
            <Button variant="outline" onClick={() => setShowResultModal(false)}>Close</Button>
            <Button onClick={() => navigate('/creative-log')} className="bg-violet-600 hover:bg-violet-700">
              <ArrowRight className="h-4 w-4 mr-2" /> View in Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Frame Lightbox */}
      <Dialog open={lightboxIdx !== null} onOpenChange={(o) => !o && closeLightbox()}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-black border-0">
          {lightboxFrame && (
            <div className="relative">
              <img
                src={`${API_URL}${lightboxFrame.url}`}
                alt={`Frame ${lightboxFrame.label}`}
                className="w-full max-h-[85vh] object-contain"
                data-testid="frame-lightbox-image"
              />
              <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-semibold flex items-center gap-2">
                <Film className="h-3.5 w-3.5" />
                Frame {lightboxFrame.label}
                <span className="text-white/60">·</span>
                <span className="font-mono">{lightboxFrame.timestamp_sec}s</span>
                <span className="text-white/60">·</span>
                <span className="text-white/80">{lightboxIdx + 1} / {frames.length}</span>
              </div>
              <button
                type="button"
                onClick={closeLightbox}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/70 text-white hover:bg-black/90 flex items-center justify-center transition-colors"
                aria-label="Close"
                data-testid="frame-lightbox-close"
              >
                <X className="h-4 w-4" />
              </button>
              {/* Prev / Next */}
              {frames.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevFrame}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center transition-colors"
                    aria-label="Previous frame"
                    data-testid="frame-lightbox-prev"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={nextFrame}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center transition-colors"
                    aria-label="Next frame"
                    data-testid="frame-lightbox-next"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              {/* Bottom thumbnail strip */}
              {frames.length > 1 && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent pt-8 pb-3 px-3">
                  <div className="flex gap-1.5 justify-center overflow-x-auto" data-testid="frame-lightbox-strip">
                    {frames.map((f, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setLightboxIdx(idx)}
                        className={`relative flex-shrink-0 w-16 h-10 rounded overflow-hidden border-2 transition-all ${idx === lightboxIdx ? 'border-white shadow-lg scale-105' : 'border-white/20 hover:border-white/60 opacity-60 hover:opacity-100'}`}
                        data-testid={`frame-lightbox-strip-${idx}`}
                      >
                        <img src={`${API_URL}${f.url}`} alt={f.label} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreativeAnalyzerPage;
